import React, { useEffect, useRef, useState } from "react";
import { Skeleton } from "@material-ui/lab";
import { styled } from "@material-ui/core";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const colors = {
  current: "#d0f0c0", 
  previous: "#f0d0d0", 
};

type ChartItem = {
  name: string;
  current_orders: number;
  current_pieces: number;
  current_amount: number;
  previous_orders: number;
  previous_pieces: number;
  previous_amount: number;
};

type Props = {
  chartData: ChartItem[];
  barCategoryGap?: number | string;
  loading?: boolean;
  isPrinting?: boolean;
};

const OrderCountChart = ({
  chartData,
  barCategoryGap = "20%",
  loading,
  isPrinting,
}: Props) => {
  const [fontSize, setFontSize] = useState(12);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.getBoundingClientRect().width;
      if (width < 477) return setFontSize(10);
      if (width > 560) return setFontSize(13);
      setFontSize(width < 532 ? 11 : 12);
    };

    handleResize();
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  if (loading) {
    return (
      <LoadingWrapper>
        {Array(10)
          .fill(0)
          .map((_, idx) => (
            <Skeleton key={idx} variant='rect' width='100%' height={10} />
          ))}
      </LoadingWrapper>
    );
  }

  return (
    <div>
      <ResponsiveContainer
        width={isPrinting ? 670 : "100%"}
        height={400}
        ref={containerRef}
      >
        <BarChart
          data={chartData.slice(0, 10)}
          barCategoryGap={barCategoryGap}
          margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
        >
          <CartesianGrid
            vertical={false}
            strokeDasharray='3 3'
            stroke='#cce5ff'
          />
          <XAxis
            dataKey='name'
            tick={{ fontSize, fill: "black", fontWeight: 600 }}
            interval={0}
            angle={-35}
            textAnchor='end'
            height={60}
          />
          <YAxis
            axisLine={{ stroke: "#cce5ff" }}
            tickLine={{ stroke: "#cce5ff" }}
            tick={{ fontSize: 12, fontWeight: 600 }}
            domain={[
              0,
              Math.ceil(
                Math.max(
                  ...chartData.map((d) =>
                    Math.max(d.current_orders, d.previous_orders)
                  )
                ) / 10
              ) * 10,
            ]}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: "rgba(0,0,0,0.05)" }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const data = payload[0].payload as ChartItem;
              return (
                <SmallTooltip>
                  <div>
                    <strong>{data.name}</strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: 600,
                    }}
                  >
                    <span style={{ color: colors.current, marginRight: 8 }}>
                      ● Current: {data.current_orders} / {data.current_pieces} /{" "}
                      {data.current_amount}
                    </span>
                    <span style={{ color: colors.previous }}>
                      ● Previous: {data.previous_orders} /{" "}
                      {data.previous_pieces} / {data.previous_amount}
                    </span>
                  </div>
                </SmallTooltip>
              );
            }}
          />

          <Bar
            dataKey='current_orders'
            fill={colors.current}
            radius={[4, 4, 0, 0]}
            barSize={24}
          />
          <Bar
            dataKey='previous_orders'
            fill={colors.previous}
            radius={[4, 4, 0, 0]}
            barSize={24}
          />
        </BarChart>
      </ResponsiveContainer>

      <LegendWrapper>
        <LegendItem>
          <CurrentYearColorBox /> Current Year
        </LegendItem>
        <LegendItem>
          <PreviousYearColorBox /> Previous Year
        </LegendItem>
      </LegendWrapper>
    </div>
  );
};

// Styled components
const TooltipWrapper = styled("div")({
  padding: 8,
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: 6,
  fontSize: 12,
});

const SmallTooltip = styled("div")({
  padding: "6px 10px",
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  borderRadius: 8,
  boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.4,
  minWidth: 140,
});

const LegendWrapper = styled("div")({
  display: "flex",
  justifyContent: "center",
  gap: 24,
  marginTop: 12,
});

const LegendItem = styled("div")({
  display: "flex",
  alignItems: "center",
  fontSize: 14,
  fontWeight: 600,
});

const CurrentYearColorBox = styled("div")({
  width: 16,
  height: 16,
  backgroundColor: "#d0f0c0", 
  marginRight: 6,
  borderRadius: 3,
});

const PreviousYearColorBox = styled("div")({
  width: 16,
  height: 16,
  backgroundColor: "#f0d0d0", 
  marginRight: 6,
  borderRadius: 3,
});

const LoadingWrapper = styled("div")({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  height: 400,
});

export default OrderCountChart;
